import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, Zap, Crown, Rocket, Gift } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import UsageMeter from "../components/UsageMeter";
import { toast } from "sonner";

const PLAN_ICONS = {
  basic: Rocket,
  pro: Zap,
  enterprise: Crown,
};

const PLAN_ACCENTS = {
  basic: { ring: "ring-blue-500/30", btn: "bg-blue-600 hover:bg-blue-700", icon: "text-blue-300" },
  pro: { ring: "ring-emerald-500/50", btn: "bg-emerald-600 hover:bg-emerald-700", icon: "text-emerald-300" },
  enterprise: { ring: "ring-purple-500/40", btn: "bg-purple-600 hover:bg-purple-700", icon: "text-purple-300" },
};

export default function Pricing() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);

  useEffect(() => {
    refresh();
  }, [lang]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/billing/plans", { params: { lang } }),
        api.get("/billing/status").catch(() => ({ data: null })),
      ]);
      setPlans(p.data.plans || []);
      setCurrent(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const startTrial = async () => {
    setStartingTrial(true);
    try {
      const r = await api.post("/billing/start-trial");
      toast.success(
        lang === "es"
          ? `Trial Pro activado · 14 días gratis`
          : `Trial Pro activat · 14 dies gratis`,
        { duration: 5000 }
      );
      setCurrent({
        ...r.data,
        plan_details: { id: r.data.plan },
        renewal_warning: false,
      });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setStartingTrial(false);
    }
  };

  const subscribe = async (planId) => {
    setPaying(planId);
    try {
      const origin = window.location.origin;
      const r = await api.post("/billing/checkout",
                               { plan: planId, origin_url: origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-16 text-center text-slate-400">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
      </div>
    );
  }

  const activePlan = current?.plan;
  const isTrialActive = current?.is_trial && current?.status === "trialing";
  const trialUsed = current?.trial_used;
  const showTrialCta = !trialUsed && !activePlan;

  return (
    <div className="space-y-8" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
          {lang === "es" ? "Planes simples para crecer" : "Plans simples per créixer"}
        </h1>
        <p className="text-slate-400 mt-3">
          {lang === "es"
            ? "Sin permanencia · IVA no incluido · Cambia o cancela cuando quieras"
            : "Sense permanència · IVA no inclòs · Canvia o cancel·la quan vulguis"}
        </p>
        {activePlan && current?.status === "active" && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm">
            <Check size={14} />
            {lang === "es" ? "Plan actual:" : "Pla actual:"} <strong className="capitalize">{activePlan}</strong>
            {current?.days_remaining != null && (
              <span className="opacity-70">· {Math.max(0, Math.floor(current.days_remaining))}d</span>
            )}
          </div>
        )}
        {isTrialActive && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/40 text-purple-200 text-sm">
            <Gift size={14} />
            {lang === "es" ? "Trial Pro activo" : "Trial Pro actiu"}
            {current?.days_remaining != null && (
              <span className="opacity-80">
                · {Math.max(0, Math.floor(current.days_remaining))}d {lang === "es" ? "restantes" : "restants"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Free trial hero CTA — only when never used */}
      {showTrialCta && (
        <div className="card-surface relative overflow-hidden border-purple-500/30 max-w-4xl mx-auto"
             data-testid="trial-hero-cta">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-blue-500/5 to-emerald-500/10 pointer-events-none" />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-[0_0_28px_rgba(168,85,247,0.45)]">
              <Gift size={24} className="text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-xs uppercase tracking-wider text-purple-300 font-bold mb-1">
                {lang === "es" ? "Oferta de lanzamiento" : "Oferta de llançament"}
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
                {lang === "es"
                  ? "14 días gratis del plan Pro"
                  : "14 dies gratis del pla Pro"}
              </h3>
              <p className="text-sm text-slate-300 mt-1">
                {lang === "es"
                  ? "Sin tarjeta · Cancela cuando quieras · Todas las funciones Pro"
                  : "Sense targeta · Cancel·la quan vulguis · Totes les funcions Pro"}
              </p>
            </div>
            <Button
              onClick={startTrial}
              disabled={startingTrial}
              className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold px-6 py-3 text-base shadow-[0_0_20px_rgba(168,85,247,0.45)]"
              data-testid="start-trial-btn"
            >
              {startingTrial ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />
                {lang === "es" ? "Activando..." : "Activant..."}</>
              ) : (
                <><Gift size={16} className="mr-2" />
                {lang === "es" ? "Activar prueba gratis" : "Activa la prova gratis"}</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Current usage meter */}
      {activePlan && (
        <div className="max-w-4xl mx-auto">
          <UsageMeter />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const Icon = PLAN_ICONS[plan.id] || Sparkles;
          const accent = PLAN_ACCENTS[plan.id] || PLAN_ACCENTS.basic;
          const isCurrent = activePlan === plan.id && current?.status === "active";
          const isPopular = plan.popular;
          return (
            <div
              key={plan.id}
              className={`card-surface p-6 relative flex flex-col ${
                isPopular ? `ring-2 ${accent.ring} scale-[1.03]` : ""
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
                  {lang === "es" ? "Más popular" : "Més popular"}
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${accent.icon}`}>
                  <Icon size={18} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white">
                  {plan.name}
                </h3>
              </div>
              <div className="mb-5">
                <span className="text-4xl font-bold text-white tabular-nums">
                  {Number(plan.price_eur).toFixed(0)}€
                </span>
                <span className="text-slate-400 text-sm">
                  {" "}/ {lang === "es" ? "mes" : "mes"}
                </span>
              </div>
              {plan.limits_text && (
                <div className={`mb-4 px-3 py-2 rounded-lg text-xs font-medium border ${
                  plan.id === "basic"
                    ? "bg-blue-500/10 border-blue-500/30 text-blue-200"
                    : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                }`}
                     data-testid={`plan-${plan.id}-limits`}>
                  {plan.id === "basic"
                    ? (lang === "es" ? "Límites: " : "Límits: ")
                    : ""}
                  <strong>{plan.limits_text}</strong>
                </div>
              )}
              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check size={14} className={`mt-0.5 flex-shrink-0 ${accent.icon}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => subscribe(plan.id)}
                disabled={!!paying || isCurrent}
                className={`w-full ${accent.btn} text-white disabled:opacity-50`}
                data-testid={`subscribe-${plan.id}`}
              >
                {paying === plan.id ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" />
                  {lang === "es" ? "Redirigiendo..." : "Redirigint..."}</>
                ) : isCurrent ? (
                  <><Check size={14} className="mr-2" />
                  {lang === "es" ? "Plan actual" : "Pla actual"}</>
                ) : (
                  lang === "es" ? "Suscribirse" : "Subscriu-te"
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="text-center text-xs text-slate-500 max-w-2xl mx-auto">
        {lang === "es"
          ? "Los pagos se procesan de forma segura mediante Stripe. Cada periodo dura 30 días y se renueva manualmente desde esta página."
          : "Els pagaments es processen de forma segura via Stripe. Cada període dura 30 dies i es renova manualment des d'aquesta pàgina."}
      </div>

      <div className="text-center">
        <button
          onClick={() => nav("/app")}
          className="text-xs text-slate-400 hover:text-white"
          data-testid="pricing-back"
        >
          ← {lang === "es" ? "Volver" : "Tornar"}
        </button>
      </div>
    </div>
  );
}
